import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import idseatLogo from '@/assets/idseat-logo.svg';
import { ClubSelectionModal } from '@/components/admin/ClubSelectionModal';
import { workspaceHome, type WorkspaceKind } from '@/lib/workspaces';
import { useEffect } from 'react';

// Generic, enumeration-safe message shown after any password-reset request,
// regardless of whether the email actually maps to an account.
const RESET_SENT_MESSAGE = "If an account exists for that email, a reset link is on its way";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showClubSelection, setShowClubSelection] = useState(false);
  // Inline "forgot password" flow: reveal an email field under the form.
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  // MFA step-up: after a valid password, a user with a verified TOTP factor must
  // clear a 6-digit challenge before we let navigation proceed. `mfaChecking`
  // blocks the post-login navigation useEffect during the async AAL check and
  // while the challenge is pending; `mfaRequired` renders the challenge step.
  const [mfaChecking, setMfaChecking] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const { signIn, signOut, workspaces, switchWorkspace, isIdseatAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  useEffect(() => {
    // Hold navigation while we resolve MFA step-up (or wait for the challenge).
    if (mfaChecking || mfaRequired) return;
    // Several workspaces and none remembered yet → let the person pick one.
    // useAuth already migrated a pre-workspace active_club_id into
    // active_workspace, so a remembered club never triggers the picker.
    if (workspaces.length > 1 && !localStorage.getItem('active_workspace') && !showClubSelection && !isIdseatAdmin) {
      setShowClubSelection(true);
      setIsLoading(false);
    } else if (workspaces.length > 0 || isIdseatAdmin) {
      // A single workspace (non-admin) becomes the active one.
      const single = workspaces.length === 1 && !isIdseatAdmin ? workspaces[0] : null;
      if (single) {
        switchWorkspace(single.kind, single.id);
      }

      // Only navigate if we're not currently showing the picker. A plain "/"
      // destination goes straight to the workspace's portal (kiosk → /kiosk).
      if (!showClubSelection) {
        navigate(single && from === '/' ? workspaceHome(single.kind) : from, { replace: true });
      }
    }
  }, [workspaces, isIdseatAdmin, navigate, from, switchWorkspace, showClubSelection, mfaChecking, mfaRequired]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Raise the navigation gate up front: the SIGNED_IN event may load profiles
    // (and trigger the nav useEffect) before our async AAL check below resolves.
    setMfaChecking(true);

    const { error } = await signIn(email, password);

    if (error) {
      // Never echo the raw provider message (it can enumerate accounts /
      // leak internals). Show a generic credentials error, except for an
      // obvious rate-limit which gets its own hint.
      const status = (error as { status?: number }).status;
      const msg = (error.message || '').toLowerCase();
      const isRateLimited =
        status === 429 || msg.includes('rate limit') || msg.includes('too many');
      toast.error(
        isRateLimited
          ? 'Too many attempts — try again in a minute'
          : 'Invalid email or password'
      );
      setMfaChecking(false);
      setIsLoading(false);
      return;
    }

    // Password is valid. If the account has a verified TOTP factor, Supabase
    // reports nextLevel 'aal2' while the fresh session is still 'aal1' — step up
    // with a TOTP challenge before allowing navigation.
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const totp = factorsData?.totp?.[0];
        if (totp) {
          setMfaFactorId(totp.id);
          setMfaCode('');
          setMfaError(null);
          setMfaRequired(true); // renders the challenge; keeps nav gated
          setMfaChecking(false);
          setIsLoading(false);
          return;
        }
      }
    } catch (err) {
      // If the AAL probe fails, don't lock the user out — fall through to the
      // normal (non-MFA) login path.
      console.error('MFA assurance-level check failed:', err);
    }

    toast.success('Welcome back!');
    // Redirection is handled by the useEffect above once profiles are loaded.
    setMfaChecking(false);
    setIsLoading(false);
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length !== 6) return;
    setMfaVerifying(true);
    setMfaError(null);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });
      if (chErr || !challenge) throw chErr ?? new Error('Challenge failed');

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: mfaCode,
      });
      if (verifyErr) throw verifyErr;

      // Session is now aal2 — release the gate and let the nav useEffect run.
      toast.success('Welcome back!');
      setMfaRequired(false);
      setMfaChecking(false);
    } catch {
      setMfaError('That code did not match. Please try again.');
      setMfaCode('');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleMfaCancel = async () => {
    // "Back to login" — drop the partial (aal1) session and reset the form.
    await signOut();
    setMfaRequired(false);
    setMfaChecking(false);
    setMfaFactorId(null);
    setMfaCode('');
    setMfaError(null);
    setPassword('');
    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;

    setSendingReset(true);
    try {
      // Fire-and-forget: we intentionally ignore the outcome so the response
      // is identical whether or not the email maps to a real account.
      // The recovery template appends ?token_hash=…&type=recovery; the confirm
      // page exchanges it on click and then opens /reset-password.
      await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/auth/confirm`,
      });
    } catch {
      // Swallow — same generic response regardless of error.
    } finally {
      toast.success(RESET_SENT_MESSAGE);
      setSendingReset(false);
      setShowForgot(false);
      setResetEmail('');
    }
  };

  const handleClubSelect = (kind: WorkspaceKind, id: string) => {
    switchWorkspace(kind, id);
    setShowClubSelection(false);
    navigate(from === '/' ? workspaceHome(kind) : from, { replace: true });
  };

  return (
    <div className="login-font min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={idseatLogo} alt="IDSeat" className="h-12" />
          </div>
          <CardTitle className="text-2xl">Admin Portal</CardTitle>
          <CardDescription>
            {mfaRequired ? 'Enter your authentication code' : 'Sign in to manage your club'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mfaRequired ? (
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-11 h-11 rounded-[12px] bg-primary/10 text-primary flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <p className="text-[14px] text-muted-foreground">
                  Open your authenticator app and enter the 6-digit code to finish signing in.
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={setMfaCode}
                  autoFocus
                  disabled={mfaVerifying}
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="rounded-[9px]" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                {mfaError && <p className="text-[13px] text-destructive">{mfaError}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={mfaVerifying || mfaCode.length !== 6}>
                {mfaVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleMfaCancel}
                  disabled={mfaVerifying}
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-60"
                >
                  Back to login
                </button>
              </div>
            </form>
          ) : (
          <>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {!showForgot ? (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setResetEmail(email);
                  setShowForgot(true);
                }}
                className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Forgot password?
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="mt-4 space-y-3 rounded-lg border border-border p-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Reset your password</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  We'll email you a link to set a new password.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" disabled={sendingReset}>
                  {sendingReset ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowForgot(false);
                    setResetEmail('');
                  }}
                  disabled={sendingReset}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
          </>
          )}
        </CardContent>
      </Card>

      <ClubSelectionModal
        isOpen={showClubSelection}
        onClose={() => setShowClubSelection(false)}
        workspaces={workspaces}
        onSelect={handleClubSelect}
      />
    </div>
  );
}
